import { Link } from 'react-router-dom';
import {
  MessageCircle,
  Clapperboard,
  Github,
  ArrowRight,
  Check,
} from 'lucide-react';
import { SiAndroid, SiLinux } from '@icons-pack/react-simple-icons';
import { AppleIcon, WindowsIcon } from '../components/PlatformIcons';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { Shot, ShotImage } from '../components/Shot';
import { StreamNookTierCard } from '../components/StreamNookTierCard';
import { cosmeticUrl } from '../lib/cosmeticUrl';
import {
  DOWNLOAD_URL,
  LINUX_DOWNLOAD_URL,
  RELEASES_URL,
  type VisitorPlatform,
} from '../lib/downloads';
import { useVisitorPlatform } from '../lib/releaseManifest';
import { SeasonalSlot, useSeasonCopy } from '../theme/seasonal';

// Every screenshot on this page is a whole app window, so it only reads if it
// is given real width: beside the copy in an even two-column split the app's
// own 13px chat text rendered around 3px.
//
// Rather than let a picture hang off the page edge and get clipped, the whole
// row widens symmetrically: WIDE_ROW centres a band on the viewport that is
// never narrower than the content box and never wider than 1600px, always with
// a gutter on both sides. `max(100%, ...)` is what keeps it from shrinking
// below the content box on a small laptop, and capping at `100vw - 96px` means
// nothing is ever cut off, on either side.
const WIDE_ROW =
  'lg:relative lg:left-1/2 lg:w-[max(100%,min(1600px,calc(100vw-96px)))] lg:-translate-x-1/2';

// The hero fills that band. `100vw - 48px` overstates it slightly in the middle
// band, which is the safe direction: it picks the larger file.
const HERO_SIZES = '(min-width: 1696px) 1600px, (min-width: 1024px) calc(100vw - 48px), calc(100vw - 48px)';
const HERO_WIDTHS = [960, 2400, 4000];

// A tour row splits that band into a 22rem copy column, a 3rem gap and the
// picture, so the picture is the band less 400px.
const TOUR_SIZES = '(min-width: 1696px) 1200px, (min-width: 1024px) calc(100vw - 448px), calc(100vw - 48px)';
const TOUR_WIDTHS = [720, 1800, 3000];

// A picture card is half of the same band, so ~788px at its widest.
const CARD_SIZES = '(min-width: 1696px) 788px, (min-width: 640px) calc(50vw - 36px), calc(100vw - 48px)';
const CARD_WIDTHS = [466, 1164, 1940];

interface GithubRelease {
  tag_name: string;
  name: string;
  published_at: string;
  html_url: string;
  assets: Array<{ name: string; browser_download_url: string; size: number }>;
}

export default function Landing() {
  const [release, setRelease] = useState<GithubRelease | null>(null);
  const heroLead = useSeasonCopy('hero.title.lead', 'Twitch,');
  const heroEm = useSeasonCopy('hero.title.em', 'made yours.');

  useEffect(() => {
    fetch('https://api.github.com/repos/StreamNook/StreamNook/releases/latest')
      .then(r => (r.ok ? r.json() : null))
      .then(data => setRelease(data as GithubRelease | null))
      .catch(() => {});
  }, []);

  return (
    <div className="space-y-28 pb-8">
      {/* ─── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative pt-6 text-center lg:pt-12">
        {/* Nothing here clips the wash: it is wider than the column so it can
            fade out at the window edge, and Layout clips it there. A clip on
            this wrapper or the section cut both of its sides off square. */}
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
          <div className="hero-beams" />
          <div className="hero-aurora" />
          <SeasonalSlot name="hero" />
        </div>

        {/* The platform list is hidden on a phone: at 375px it wrapped the pill
            onto two lines and left the accent dot floating beside the gap. */}
        <span className="inline-flex items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-1 text-[12px] font-medium text-textSecondary">
          <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-accent" />
          <span>
            Free and open source
            <span className="hidden sm:inline"> · Windows, macOS, Linux, Android and iPhone</span>
          </span>
        </span>

        <h1 className="font-serif mx-auto mt-6 max-w-4xl text-[2.75rem] font-normal leading-[0.98] tracking-[-0.01em] text-white sm:text-6xl md:text-7xl lg:text-[88px]">
          {heroLead} <span className="italic">{heroEm}</span>
        </h1>

        <p className="mx-auto mt-5 max-w-2xl leading-relaxed text-textSecondary sm:mt-6 sm:text-lg">
          A native Twitch client built in Rust and React, faster and lighter than a browser tab.
          Watch several streams at once, pull chat into its own window, and see every emote your
          channels actually use.
        </p>

        <div className="mx-auto mt-8 w-full max-w-sm sm:mt-9 sm:max-w-none">
          <PlatformActions
            tag={release?.tag_name}
            trailing={
              <Link
                to="/support"
                className="px-4 py-3 text-center text-sm text-textSecondary transition-colors hover:text-textPrimary"
              >
                Support the project →
              </Link>
            }
          />
        </div>

        <div className="mt-10 sm:mt-16">
          <Shot
            name="multinook"
            alt="StreamNook's MultiNook view: four live Twitch streams in a grid with a chat column beside them"
            widths={HERO_WIDTHS}
            width={4800}
            height={2660}
            sizes={HERO_SIZES}
            className={WIDE_ROW}
            priority
          />
        </div>
        <p className="mt-4 text-[12px] text-textMuted">
          MultiNook: as many streams as you want, each with its own chat.
        </p>
      </section>

      {/* ─── Feature tour ─────────────────────────────────────────────── */}
      <TourRow
        title="Chat, even without the stream."
        body="Use StreamNook as a pure chat client. Add channels as tabs, or split a window into two, three, or four columns to read them all at once. Run a window per monitor, and it keeps flowing while everything else is tucked into the tray."
        bullets={[
          'Tabs or 2 to 4 column splits',
          'A mod log and activity feed under the columns',
          'A window per monitor',
          'Runs in the background',
        ]}
        media={
          <Shot
            name="multichat"
            alt="A StreamNook MultiChat window: two channels side by side, the mod log listing timeouts, bans and deleted messages beneath them, and the subscriber activity feed below that"
            widths={TOUR_WIDTHS}
            width={3800}
            height={2536}
            sizes={TOUR_SIZES}
          />
        }
      />

      <TourRow
        reverse
        title="The chat, done right."
        body="Every 7TV, BetterTTV, and FrankerFaceZ emote with animation and zero-width overlays. Subs, Hype Trains, Predictions, bits, and pinned messages rendered the way Twitch shows them. Paints and badges travel with you across the community."
        bullets={['Every emote provider, no flicker', 'Highlight phrases + custom commands', 'Per-user nicknames and notes']}
        media={
          <Shot
            name="chat"
            alt="A Twitch stream playing in StreamNook with its chat alongside, showing badges, paints, emotes and replies"
            widths={TOUR_WIDTHS}
            width={4800}
            height={2322}
            sizes={TOUR_SIZES}
          />
        }
      />

      <TourRow
        title="Every drop, in one grid."
        body="Every Twitch drop campaign you can earn from, with what you have already claimed and what is still running. Your inventory, your progress and your channel points sit in the same window, so you can see what is worth leaving on."
        bullets={[
          'Every campaign, claimed and running',
          'Inventory and progress together',
          'Search by game, sort by what is worth it',
        ]}
        media={
          <Shot
            name="drops"
            alt="StreamNook's Drops and Channel Points view: a grid of Twitch drop campaigns with box art, campaign counts and claim progress"
            widths={TOUR_WIDTHS}
            width={3392}
            height={1795}
            sizes={TOUR_SIZES}
          />
        }
      />

      {/* ─── Everything else ──────────────────────────────────────────── */}
      <section className={WIDE_ROW}>
        {/* The eyebrow and the title must not say the same thing. "And the rest"
            over "The rest of the app." read as one phrase printed twice. */}
        <SectionHeading eyebrow="More" title="The rest of the app." />

        {/* Four of these earn a picture. They are single panels rather than
            whole windows, so unlike the tour shots they stay readable at half
            the band's width. */}
        <div className="mt-10 grid gap-6 sm:grid-cols-2">
          <PictureCard
            name="badges"
            title="Badge tracker"
            body="Every Twitch and 7TV cosmetic, tracked and dated, so you can see what you are missing and swap what you wear."
            alt="StreamNook's cosmetics browser: a grid of Twitch badges with a collected count and filters for what is available now"
            width={1930}
            height={1515}
          />
          <PictureCard
            name="palette"
            title="Command palette"
            body="Jump to any channel, command, or setting from a single Ctrl+K shortcut, with your own snippets and aliases."
            alt="StreamNook's command palette open over the app, listing recent items and jump-to actions with their keyboard shortcuts"
            width={1568}
            height={1215}
            // The capture is 1568 wide, so its largest size is that, not 1940.
            widths={[466, 1164, 1568]}
          />
          <PictureCard
            name="overlay"
            title="Stream overlay for OBS"
            body="Design a chat overlay, point an OBS browser source at it, and read Twitch, Kick, YouTube and TikTok in one column."
            alt="StreamNook's stream overlay designer showing a live preview of chat from several platforms rendered for OBS"
            width={1354}
            height={1291}
          />
          <PictureCard
            name="theme"
            title="24 themes and a creator"
            body="Winters' Glass and 23 more, from a glassiness slider down to the font, plus a full builder with live preview."
            alt="StreamNook's theme editor: a glassiness slider, a font picker and a row of signature themes with their colour swatches"
            width={2381}
            height={1568}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <MiniFeature
            icon={<MessageCircle className="h-5 w-5" />}
            title="Whispers"
            body="Send and receive with a window per conversation, full history, and live incoming pings."
          />
          <MiniFeature
            icon={<Clapperboard className="h-5 w-5" />}
            title="VODs & offline chat"
            body="Watch past broadcasts like live streams, and chat in any offline channel without a browser."
          />
        </div>
      </section>

      {/* ─── Membership ───────────────────────────────────────────────── */}
      <section className="settings-card overflow-hidden">
        <div className="grid lg:grid-cols-2">
          <div className="flex flex-col justify-center gap-4 p-8 sm:p-10">
            <h2 className="text-3xl font-semibold tracking-tight">Wear your number.</h2>
            <p className="max-w-md leading-relaxed text-textSecondary">
              Every account gets a permanent member number and tier the moment you sign in, shown
              only to other StreamNook viewers. Support the project and the gold StreamNook badge
              rides along on every message you send, across the whole community.
            </p>
            <div className="pt-1">
              <Link
                to="/support"
                className="glass-button inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium"
              >
                Support the project <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-6 border-t border-white/[0.05] p-10 lg:border-l lg:border-t-0">
            <div className="flex flex-col items-center gap-2">
              <img
                src={cosmeticUrl('streamnook-badge-gold-animated.webp')}
                alt="Animated gold StreamNook supporter badge"
                className="h-16 w-16 object-contain"
              />
              <span className="text-[10px] uppercase tracking-[0.28em] text-textMuted">
                Supporter badge
              </span>
            </div>
            <StreamNookTierCard userNumber={358} cosmeticName="Gold" />
          </div>
        </div>
      </section>

      {/* ─── Closing CTA ──────────────────────────────────────────────── */}
      <section className="settings-card p-10 text-center sm:p-14">
        <h2 className="text-3xl font-semibold tracking-tight">Get StreamNook</h2>
        <p className="mx-auto mt-3 max-w-md leading-relaxed text-textSecondary">
          Free and open source on Windows, macOS, Linux and Android, and in beta on iPhone.
        </p>
        <div className="mx-auto mt-7 w-full max-w-sm sm:max-w-none">
          <PlatformActions
            tag={release?.tag_name}
            trailing={
              <a
                href={RELEASES_URL}
                target="_blank"
                rel="noreferrer"
                className="glass-button inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-medium"
              >
                <Github className="h-4 w-4" />
                View on GitHub
              </a>
            }
          />
        </div>
      </section>
    </div>
  );
}

/**
 * Download actions for every platform, ordered so the build the visitor can
 * actually install comes first and is the one filled button. The others stay
 * one click away either way, so a wrong guess costs nothing.
 *
 * Windows and Linux download in place. macOS, Android and iPhone route to
 * /downloads on purpose: each needs its first-launch steps alongside the build
 * (Gatekeeper's Open Anyway on a Mac, the unknown-sources prompt on Android,
 * installing TestFlight before the invite on an iPhone), not a bare link that
 * then refuses to open. Linux needs no such page: an AppImage is marked
 * executable and run, with nothing to explain that the file itself does not
 * already say.
 *
 * Layout: on a phone the visitor's own build is a full-width button and the
 * others share a two-column grid beneath it, so they can never wrap into a
 * ragged staircase with a short label like "macOS" stranded beside the support
 * link. Four fill the grid evenly; should the count ever be odd, the last
 * button spans the full width rather than sitting half-width and alone. At
 * `sm` and up the inner wrapper becomes `display: contents` and every button
 * rejoins the parent row (`col-span` is inert on a flex item, so the span
 * class needs no breakpoint reset). `trailing` is whatever follows the buttons (the
 * support link, the GitHub link): full width on a phone, inline from `sm` up,
 * for the same reason.
 */
function PlatformActions({ tag, trailing }: { tag?: string; trailing?: ReactNode }) {
  const visitor = useVisitorPlatform();
  const style = (platform: VisitorPlatform) =>
    `${visitor === platform ? 'btn-primary' : 'glass-button'} inline-flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold lg:px-5`;
  const versionTag = (platform: VisitorPlatform) =>
    tag && visitor === platform ? (
      <span className="ml-1 text-[11px] font-normal opacity-70">{tag}</span>
    ) : null;

  // Only the visitor's own platform spells out the full call to action; the
  // others carry the bare platform name, which is what lets them sit in a
  // half-width column on a phone without wrapping onto two lines. The iPhone
  // button also drops "beta" below `xl`: five buttons with two of them saying
  // "beta" broke a narrow phone column and spilled the hero row onto a second
  // line at 1024px. The /downloads card it leads to carries the Beta badge.
  const betaTag = <span className="hidden xl:inline"> beta</span>;
  const windows = (
    <a key="windows" href={DOWNLOAD_URL} download className={style('windows')}>
      <WindowsIcon className="h-3.5 w-3.5" />
      {visitor === 'windows' ? 'Download for Windows' : 'Windows'}
      {versionTag('windows')}
    </a>
  );

  const mac = (
    <Link key="mac" to="/downloads" className={style('mac')}>
      <AppleIcon className="h-4 w-4" />
      {visitor === 'mac' ? 'Download for macOS' : 'macOS'}
      {versionTag('mac')}
    </Link>
  );

  const linux = (
    <a key="linux" href={LINUX_DOWNLOAD_URL} download className={style('linux')}>
      <SiLinux className="h-4 w-4" />
      {visitor === 'linux' ? 'Download for Linux' : 'Linux'}
    </a>
  );

  const android = (
    <Link key="android" to="/downloads" className={style('android')}>
      <SiAndroid className="h-4 w-4" />
      {visitor === 'android' ? 'Download for Android' : 'Android'}
    </Link>
  );

  // TestFlight holds the build, so the button leads to the /downloads card that
  // explains it (install TestFlight, then open the invite), never the invite.
  const iphone = (
    <Link key="iphone" to="/downloads" className={style('iphone')}>
      <AppleIcon className="h-4 w-4" />
      {visitor === 'iphone' ? 'Get the iPhone beta' : <span>iPhone{betaTag}</span>}
    </Link>
  );

  // Keyed by every VisitorPlatform, and `Record` rather than an inferred object
  // literal so that widening the union is a TYPE ERROR here rather than an
  // `undefined` that destructures into a blank page for the new platform's
  // visitors. That is exactly how Linux would have shipped broken.
  const order: Record<VisitorPlatform, ReactNode[]> = {
    windows: [windows, mac, linux, android, iphone],
    mac: [mac, windows, linux, android, iphone],
    linux: [linux, windows, mac, android, iphone],
    android: [android, windows, mac, linux, iphone],
    iphone: [iphone, windows, mac, linux, android],
  };
  const [primary, ...secondary] = order[visitor];
  return (
    <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center">
      {primary}
      <div className="grid grid-cols-2 gap-3 sm:contents [&>*:last-child:nth-child(odd)]:col-span-2">
        {secondary}
      </div>
      {trailing}
    </div>
  );
}

function TourRow({
  eyebrow,
  title,
  body,
  bullets,
  media,
  reverse = false,
}: {
  eyebrow?: string;
  title: string;
  body: string;
  bullets: string[];
  media: ReactNode;
  reverse?: boolean;
}) {
  // Copy one side, screenshot the other, alternating down the page. The copy is
  // held to a 22rem column and the row itself widens past the text measure, so
  // the screenshot gets ~1200px on a 1440p monitor rather than the 520px an
  // even split of the content box gave it, without any of it being cut off.
  return (
    <section>
      <div
        className={`grid items-center gap-x-12 gap-y-8 ${WIDE_ROW} ${
          reverse
            ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)]'
            : 'lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]'
        }`}
      >
        <div className={reverse ? 'lg:order-2' : ''}>
          {eyebrow && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-textMuted">
              {eyebrow}
            </span>
          )}
          <h2 className="mt-2 text-3xl font-semibold tracking-tight">{title}</h2>
          <p className="mt-4 leading-relaxed text-textSecondary">{body}</p>
          <ul className="mt-5 space-y-2">
            {bullets.map(b => (
              <li key={b} className="flex items-center gap-2.5 text-sm text-textSecondary">
                <Check className="h-4 w-4 flex-shrink-0 text-accent" />
                {b}
              </li>
            ))}
          </ul>
        </div>
        <div className={reverse ? 'lg:order-1' : ''}>{media}</div>
      </div>
    </section>
  );
}

function SectionHeading({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div className="text-center">
      <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-textMuted">
        {eyebrow}
      </span>
      <h2 className="mt-2 text-3xl font-semibold tracking-tight">{title}</h2>
    </div>
  );
}

/**
 * A feature card with a real screenshot above the copy.
 *
 * The picture is held in a uniform 3:2 box with `object-cover object-top`:
 * the four panels have quite different shapes, and cropping each one to an
 * identical aspect by hand would either cut something important or leave the
 * grid ragged. Anchoring to the top keeps the part that identifies the panel.
 */
function PictureCard({
  name,
  title,
  body,
  alt,
  width,
  height,
  widths = CARD_WIDTHS,
}: {
  name: string;
  title: string;
  body: string;
  alt: string;
  width: number;
  height: number;
  /** The sizes built for this card, when its capture is too small for the
   *  usual ladder (the build never upscales). */
  widths?: number[];
}) {
  return (
    <div className="liquid-glass-panel overflow-hidden">
      <ShotImage
        name={name}
        alt={alt}
        widths={widths}
        width={width}
        height={height}
        sizes={CARD_SIZES}
        className="aspect-[3/2] w-full object-cover object-top"
      />
      <div className="border-t border-white/[0.05] p-5">
        <h3 className="text-[15px] font-semibold tracking-tight">{title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-textSecondary">{body}</p>
      </div>
    </div>
  );
}

function MiniFeature({ icon, title, body }: { icon: ReactNode; title: string; body: string }) {
  return (
    <div className="glass-panel p-6">
      <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--color-surface)] text-accent">
        {icon}
      </div>
      <h3 className="mt-4 text-base font-semibold text-textPrimary">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-textSecondary">{body}</p>
    </div>
  );
}
